import { useCallback, useEffect, useMemo, useState } from "react";
import api, { fetchAuthOpts, formatApiError } from "@/lib/api";
import { workedTodaySeconds, pausedTodaySeconds } from "@/lib/timeClock";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Clock, Play, Square, Pause, Coffee, ChevronLeft, ChevronRight,
  Download, FileText, ShieldAlert, Edit3, X, Save, History,
} from "lucide-react";

const STATE_LABEL = {
  sin_iniciar: "Sin iniciar",
  trabajando: "Trabajando",
  en_pausa: "En pausa",
  finalizado: "Jornada finalizada",
};
const STATE_COLOR = {
  sin_iniciar: "bg-neutral-100 text-neutral-700 border-neutral-300",
  trabajando: "bg-emerald-50 text-emerald-800 border-emerald-300",
  en_pausa: "bg-amber-50 text-amber-800 border-amber-300",
  finalizado: "bg-neutral-200 text-neutral-700 border-neutral-400",
};
const KIND_LABEL = {
  entrada: "Entrada",
  inicio_pausa: "Inicio pausa",
  fin_pausa: "Fin pausa",
  salida: "Salida",
};
const METHOD_LABEL = {
  manual: "Manual",
  login_confirmed: "Confirmado tras login",
  login_auto: "Automático tras login",
  admin_correction: "Corrección admin",
};
const INCIDENCIA_LABEL = {
  entrada_duplicada: "Entrada duplicada",
  inicio_pausa_invalido: "Inicio de pausa inválido",
  fin_pausa_sin_inicio: "Fin de pausa sin inicio",
  pausa_negativa: "Pausa con duración negativa",
  salida_sin_entrada: "Salida sin entrada",
  salida_con_pausa_abierta: "Salida con pausa abierta",
  salida_duplicada: "Salida duplicada",
  pausa_abierta: "Pausa abierta",
  jornada_sin_cerrar: "Jornada sin cerrar",
  falta_entrada: "Falta entrada",
  falta_salida: "Falta salida",
  jornada_mayor_16h: "Jornada superior a 16 h",
};

function fmtHMS(seconds) {
  if (seconds === null || seconds === undefined) return "—";
  seconds = Math.max(0, Math.floor(seconds));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}
function fmtHourFromIso(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Madrid" });
  } catch { return "—"; }
}

export default function TimeClock() {
  const { user, isAdmin } = useAuth();
  const [tab, setTab] = useState("today");

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-6 lg:px-10 pt-6 lg:pt-8 pb-4 border-b border-[#E5E5E0] bg-[#F7F7F4]">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#6E8B3D]">
              // Registro horario · hora del servidor (Madrid)
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-black mt-2">Control horario</h1>
            <p className="text-sm text-[#525252] mt-1">
              Hola <span className="font-semibold">{user?.name}</span>. Tus fichajes quedan registrados a tu nombre y con la hora exacta del servidor.
            </p>
          </div>
        </div>
        <div className="mt-4 inline-flex border border-[#111110]">
          {[
            { v: "today", l: "Hoy", I: Clock },
            { v: "month", l: "Mensual", I: FileText },
            ...(isAdmin() ? [{ v: "admin", l: "Admin", I: ShieldAlert }] : []),
          ].map(({ v, l, I }) => (
            <button
              key={v}
              onClick={() => setTab(v)}
              data-testid={`tc-tab-${v}`}
              className={`px-3 py-2 text-xs font-mono uppercase tracking-wider inline-flex items-center gap-1.5 ${
                tab === v ? "bg-[#111110] text-white" : "bg-white"
              }`}
            >
              <I size={12} /> {l}
            </button>
          ))}
        </div>
      </header>

      <div className="flex-1 p-6 lg:p-10">
        {tab === "today" && <TodayView />}
        {tab === "month" && <MonthView selfUserId={user?.id} canPickUser={isAdmin()} />}
        {tab === "admin" && isAdmin() && <AdminView />}
      </div>
    </div>
  );
}

function TodayView() {
  const [data, setData] = useState(null);
  const [now, setNow] = useState(new Date());
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/time-clock/today");
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => {
    load();
    // Polling cada 30 s: misma cadencia que el reloj de cabecera para que
    // ambos consumidores muestren EXACTAMENTE el mismo `total_trabajado_s`.
    const poll = setInterval(load, 30000);
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(poll); clearInterval(t); };
  }, [load]);

  const action = async (path, label) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(label + " registrada");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  if (!data) return <div className="text-sm text-[#525252]">Cargando...</div>;

  const state = data.state;
  // Fuente única de verdad: ver `lib/timeClock.js`.
  const totalTrabLive = workedTodaySeconds(data, now.getTime());
  const totalPausaLive = pausedTodaySeconds(data, now.getTime());

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Big clock + state + actions */}
      <div className="lg:col-span-2 ds-card p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Fecha</div>
            <div className="font-display text-xl font-bold mt-1">
              {now.toLocaleDateString("es", { weekday: "long", day: "2-digit", month: "long", year: "numeric", timeZone: "Europe/Madrid" })}
            </div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">Hora servidor</div>
            <div className="font-display font-black text-4xl tabular-nums" data-testid="tc-clock-time">
              {now.toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/Madrid" })}
            </div>
          </div>
        </div>

        <div className="mt-5">
          <span className={`inline-flex items-center px-3 py-1 border font-mono text-[11px] uppercase tracking-widest ${STATE_COLOR[state]}`}>
            <span className="w-2 h-2 mr-2 inline-block rounded-full bg-current opacity-70"></span>
            {STATE_LABEL[state] || state}
          </span>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3">
          {state === "sin_iniciar" && (
            <button
              onClick={() => action("/time-clock/clock-in", "Entrada")}
              disabled={busy}
              data-testid="tc-clockin"
              className="col-span-2 inline-flex items-center justify-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-3 text-sm font-semibold disabled:opacity-50"
            >
              <Play size={14} /> Fichar entrada
            </button>
          )}
          {state === "trabajando" && (
            <>
              <button
                onClick={() => action("/time-clock/start-break", "Pausa")}
                disabled={busy}
                data-testid="tc-startbreak"
                className="inline-flex items-center justify-center gap-2 border border-amber-700 text-amber-900 bg-amber-50 hover:bg-amber-100 px-4 py-3 text-sm font-semibold disabled:opacity-50"
              >
                <Coffee size={14} /> Iniciar pausa
              </button>
              <button
                onClick={() => action("/time-clock/clock-out", "Salida")}
                disabled={busy}
                data-testid="tc-clockout"
                className="inline-flex items-center justify-center gap-2 bg-[#111110] hover:bg-black text-white px-4 py-3 text-sm font-semibold disabled:opacity-50"
              >
                <Square size={14} /> Fichar salida
              </button>
            </>
          )}
          {state === "en_pausa" && (
            <button
              onClick={() => action("/time-clock/end-break", "Fin de pausa")}
              disabled={busy}
              data-testid="tc-endbreak"
              className="col-span-2 inline-flex items-center justify-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-3 text-sm font-semibold disabled:opacity-50"
            >
              <Pause size={14} /> Finalizar pausa
            </button>
          )}
          {state === "finalizado" && (
            <div className="col-span-2 text-center py-3 border border-[#E5E5E0] bg-[#F7F7F4] text-sm text-[#525252]">
              Jornada finalizada. Solo un administrador puede modificarla.
            </div>
          )}
        </div>

        {/* Summary stats */}
        <div className="mt-6 grid grid-cols-4 gap-3">
          <Stat label="Entrada" value={fmtHourFromIso(data.entrada)} mono />
          <Stat label="Salida" value={fmtHourFromIso(data.salida)} mono />
          <Stat label="Pausas" value={`${data.num_pausas} · ${fmtHMS(totalPausaLive)}`} mono />
          <Stat label="Trabajado" value={fmtHMS(totalTrabLive)} mono strong />
        </div>

        {data.incidencias && data.incidencias.length > 0 && (
          <div className="mt-4 bg-red-50 border border-red-300 text-red-800 px-3 py-2 text-xs">
            <span className="font-semibold">Incidencias:</span>{" "}
            {data.incidencias.map((i) => INCIDENCIA_LABEL[i] || i).join(" · ")}
          </div>
        )}
      </div>

      {/* Events of the day */}
      <div className="ds-card p-6">
        <div className="font-display font-bold text-sm mb-3">Eventos de hoy</div>
        {(!data.events || data.events.length === 0) ? (
          <div className="text-xs text-[#525252]">Sin eventos todavía.</div>
        ) : (
          <ul className="space-y-2">
            {data.events.map((e) => (
              <li key={e.id} className="flex items-start gap-2 text-xs border-l-2 border-[#6E8B3D] pl-3">
                <div className="flex-1">
                  <div className="font-semibold">{KIND_LABEL[e.kind] || e.kind}</div>
                  <div className="font-mono text-[10px] text-[#525252]">
                    {fmtHourFromIso(e.at_corrected || e.at)} · {METHOD_LABEL[e.method] || e.method}
                    {e.corrected && <span className="ml-1 text-amber-700">· corregido</span>}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, mono, strong }) {
  return (
    <div className="bg-[#F7F7F4] border border-[#E5E5E0] px-3 py-2">
      <div className="font-mono text-[9px] uppercase tracking-widest text-[#525252]">{label}</div>
      <div className={`mt-1 ${mono ? "font-mono" : ""} ${strong ? "font-bold text-base" : "text-sm"}`}>{value}</div>
    </div>
  );
}

function MonthView({ selfUserId, canPickUser }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [userId, setUserId] = useState(selfUserId);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (canPickUser) {
      api.get("/users", { params: { active: "all" } }).then((r) => setUsers(r.data));
    }
  }, [canPickUser]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/time-clock/month", { params: { year, month, user_id: userId } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [year, month, userId]);
  useEffect(() => { load(); }, [load]);

  const prev = () => {
    if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1);
  };
  const next = () => {
    if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1);
  };

  const monthLabel = useMemo(() => {
    const d = new Date(year, month - 1, 1);
    return d.toLocaleDateString("es", { month: "long", year: "numeric" });
  }, [year, month]);

  const doExport = async (format) => {
    try {
      const r = await fetch(`${api.defaults.baseURL}/time-clock/export?format=${format}&year=${year}&month=${month}&user_id=${userId || ""}`, fetchAuthOpts());
      if (!r.ok) { toast.error("Error exportando"); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `control-horario-${year}-${String(month).padStart(2, "0")}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error exportando"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="inline-flex items-center gap-2">
          <button onClick={prev} data-testid="tc-month-prev" className="border border-[#111110] hover:bg-[#111110] hover:text-white p-2"><ChevronLeft size={14} /></button>
          <div className="font-display font-bold text-lg capitalize px-3" data-testid="tc-month-label">{monthLabel}</div>
          <button onClick={next} data-testid="tc-month-next" className="border border-[#111110] hover:bg-[#111110] hover:text-white p-2"><ChevronRight size={14} /></button>
        </div>
        {canPickUser && (
          <select
            value={userId || ""}
            onChange={(e) => setUserId(e.target.value)}
            data-testid="tc-month-user"
            className="ds-card outline-none px-3 py-2 text-sm rounded-none"
          >
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
            ))}
          </select>
        )}
        <div className="inline-flex gap-1">
          <button onClick={() => doExport("csv")} data-testid="tc-export-csv" className="border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-xs font-mono uppercase inline-flex items-center gap-1">
            <Download size={12} /> CSV
          </button>
          <button onClick={() => doExport("pdf")} data-testid="tc-export-pdf" className="border border-[#111110] hover:bg-[#111110] hover:text-white px-3 py-2 text-xs font-mono uppercase inline-flex items-center gap-1">
            <Download size={12} /> PDF
          </button>
        </div>
      </div>

      {!data ? <div className="text-sm text-[#525252]">Cargando...</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <Stat label="Total trabajado" value={fmtHMS(data.summary.total_trabajado_s)} mono strong />
            <Stat label="Total pausas" value={fmtHMS(data.summary.total_pausa_s)} mono />
            <Stat label="Días trabajados" value={data.summary.dias_trabajados} mono />
            <Stat label="Días con incidencias" value={data.summary.dias_incidencias} mono />
          </div>
          <div className="ds-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b-2 border-[#111110]">
                  <th className="px-3 py-2">Día</th>
                  <th className="px-3 py-2">Entrada</th>
                  <th className="px-3 py-2">Salida</th>
                  <th className="px-3 py-2 text-right">Bruto</th>
                  <th className="px-3 py-2 text-right">Pausas</th>
                  <th className="px-3 py-2 text-right">Trabajado</th>
                  <th className="px-3 py-2">Incidencias</th>
                  {isAdmin() && <th className="px-3 py-2 text-right">Acciones</th>}
                </tr>
              </thead>
              <tbody>
                {data.days.map((r) => (
                  <tr key={r.day} className={`border-b border-[#E5E5E0] ${r.empty ? "text-[#a0a0a0]" : ""}`}>
                    <td className="px-3 py-2 font-mono text-xs">{r.day}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.empty ? "—" : fmtHourFromIso(r.entrada)}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.empty ? "—" : fmtHourFromIso(r.salida)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-right">{r.empty ? "—" : fmtHMS(r.total_bruto_s)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-right">{r.empty ? "—" : fmtHMS(r.total_pausa_s)}</td>
                    <td className="px-3 py-2 font-mono text-xs text-right font-bold">{r.empty ? "—" : fmtHMS(r.total_trabajado_s)}</td>
                    <td className="px-3 py-2 text-[10px]">
                      {(r.incidencias || []).map((i) => (
                        <span key={i} className="inline-block mr-1 mb-0.5 px-1 py-0.5 bg-red-50 text-red-700 border border-red-200 font-mono">
                          {INCIDENCIA_LABEL[i] || i}
                        </span>
                      ))}
                    </td>
                    {isAdmin() && (
                      <td className="px-3 py-2 text-right">
                        {!r.empty && (r.events?.length > 0) && (
                          <DayCorrectionsButton day={r.day} events={r.events} onChanged={load} />
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function DayCorrectionsButton({ day, events, onChanged }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`tc-day-correct-${day}`} className="border border-[#111110] hover:bg-[#111110] hover:text-white px-2 py-1 text-[10px] font-mono uppercase inline-flex items-center gap-1">
        <Edit3 size={11} /> Corregir
      </button>
      {open && <CorrectDayDialog day={day} events={events} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); onChanged(); }} />}
    </>
  );
}

function CorrectDayDialog({ day, events, onClose, onSaved }) {
  const [targetId, setTargetId] = useState(events[0]?.id || "");
  const target = events.find((e) => e.id === targetId);
  const initial = target ? (target.at_corrected || target.at) : "";
  // datetime-local needs YYYY-MM-DDTHH:MM
  const toLocal = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const tz = new Date(d.toLocaleString("en-US", { timeZone: "Europe/Madrid" }));
    const pad = (n) => String(n).padStart(2, "0");
    return `${tz.getFullYear()}-${pad(tz.getMonth() + 1)}-${pad(tz.getDate())}T${pad(tz.getHours())}:${pad(tz.getMinutes())}`;
  };
  const [newAt, setNewAt] = useState(toLocal(initial));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);

  // Sólo re-ejecutar cuando cambia el evento target (targetId), no cuando
  // `target` se re-evalúa por otros re-renders.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setNewAt(toLocal(target?.at_corrected || target?.at)); }, [targetId]);

  useEffect(() => {
    if (!targetId) { setHistory([]); return; }
    api.get(`/time-clock/events/${targetId}/history`).then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, [targetId]);

  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) { toast.error("El motivo es obligatorio (mín. 5 caracteres)"); return; }
    if (!newAt) { toast.error("Indica la nueva fecha y hora"); return; }
    setSaving(true);
    try {
      // Convert local (Madrid) to ISO with offset
      const at = `${newAt}:00`; // YYYY-MM-DDTHH:MM:00 (interpreted as Madrid in backend since no tz)
      await api.post(`/time-clock/events/${targetId}/correct`, { at_corrected: at, reason: reason.trim() });
      toast.success("Fichaje corregido");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-[#111110] shadow-hard w-full max-w-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div className="font-display font-bold text-sm">Corregir fichaje · {day}</div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]"><X size={14} /></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <div>
            <label className="font-mono text-[10px] uppercase text-[#525252]">Evento</label>
            <select
              value={targetId}
              onChange={(e) => setTargetId(e.target.value)}
              data-testid="tc-correct-event"
              className="mt-1 w-full ds-card outline-none px-3 py-2 text-sm rounded-none"
            >
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {KIND_LABEL[e.kind]} · {fmtHourFromIso(e.at_corrected || e.at)} {e.corrected ? "(corregido)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase text-[#525252]">Nueva fecha/hora (Madrid)</label>
            <input
              type="datetime-local"
              value={newAt}
              onChange={(e) => setNewAt(e.target.value)}
              data-testid="tc-correct-newat"
              className="mt-1 w-full ds-card outline-none px-3 py-2 text-sm rounded-none"
            />
            {target?.at && (
              <div className="text-[10px] text-[#525252] mt-1 font-mono">
                Original: {fmtHourFromIso(target.at)} ({target.at})
              </div>
            )}
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase text-[#525252]">Motivo (obligatorio, mín. 5 caracteres)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              required
              data-testid="tc-correct-reason"
              placeholder="Ej. El empleado olvidó fichar salida"
              className="mt-1 w-full ds-card outline-none px-3 py-2 text-sm rounded-none"
            />
          </div>

          {history.length > 0 && (
            <div className="border-t border-[#E5E5E0] pt-3">
              <div className="font-mono text-[10px] uppercase text-[#525252] mb-1.5 inline-flex items-center gap-1">
                <History size={11} /> Historial de correcciones
              </div>
              <ul className="space-y-1 text-[11px]">
                {history.map((h) => (
                  <li key={h.id} className="border-l-2 border-amber-500 pl-2">
                    <div className="font-mono">
                      {new Date(h.at).toLocaleString("es")} · {h.by_user_email}
                    </div>
                    <div className="text-[#525252]">
                      {fmtHourFromIso(h.previous_at)} → {fmtHourFromIso(h.corrected_at)} · {h.reason}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button type="submit" disabled={saving} data-testid="tc-correct-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-3 py-2.5 text-sm font-semibold disabled:opacity-50">
            <Save size={14} /> {saving ? "Guardando..." : "Guardar corrección"}
          </button>
        </form>
      </div>
    </div>
  );
}

function AdminView() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/time-clock/admin/month", { params: { year, month } });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const monthLabel = useMemo(() => new Date(year, month - 1, 1).toLocaleDateString("es", { month: "long", year: "numeric" }), [year, month]);
  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={prev} className="border border-[#111110] hover:bg-[#111110] hover:text-white p-2"><ChevronLeft size={14} /></button>
        <div className="font-display font-bold text-lg capitalize px-3">{monthLabel}</div>
        <button onClick={next} className="border border-[#111110] hover:bg-[#111110] hover:text-white p-2"><ChevronRight size={14} /></button>
      </div>

      {!data ? <div className="text-sm text-[#525252]">Cargando...</div> : (
        <div className="ds-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#525252] border-b-2 border-[#111110]">
                <th className="px-3 py-2">Usuario</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2 text-right">Días trabajados</th>
                <th className="px-3 py-2 text-right">Total trabajado</th>
                <th className="px-3 py-2 text-right">Total pausas</th>
                <th className="px-3 py-2 text-right">Días con incidencias</th>
              </tr>
            </thead>
            <tbody>
              {data.users.map((u) => (
                <tr key={u.user_id} className={`border-b border-[#E5E5E0] ${!u.active ? "opacity-50" : ""}`} data-testid={`tc-admin-row-${u.user_id}`}>
                  <td className="px-3 py-2 font-semibold">{u.user_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{u.user_email}</td>
                  <td className="px-3 py-2 font-mono text-xs text-right">{u.dias_trabajados}</td>
                  <td className="px-3 py-2 font-mono text-xs text-right font-bold">{fmtHMS(u.total_trabajado_s)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-right">{fmtHMS(u.total_pausa_s)}</td>
                  <td className="px-3 py-2 font-mono text-xs text-right">
                    {u.dias_incidencias > 0 ? (
                      <span className="text-red-700 font-bold">{u.dias_incidencias}</span>
                    ) : (
                      "0"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
